import React from 'react';
import { DataGridPro, GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiRef } from '@mui/x-data-grid-pro';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Colors from '../../../library/Colors';

// Default columns configuration
const DEFAULT_COLUMNS = [
    {
        field: 'id',
        headerName: "ID",
        width: 50,
        align: "left",
        display: 'flex',
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: 125,
        align: "left",
        display: 'flex',
    },
    {
        field: 'vat',
        headerName: 'VAT',
        minWidth: 150,
        align: "left",
        display: 'flex',
    },
    {
        field: 'address1',
        headerName: 'Address',
        minWidth: 150,
        align: "left",
        display: 'flex',
    },
    {
        field: 'address2',
        headerName: 'Address 2',
        minWidth: 100,
        align: "left",
        display: 'flex',
    },
    {
        field: 'city',
        headerName: 'City',
        minWidth: 100,
        align: "left",
        display: 'flex',
    },
    {
        field: 'zip',
        headerName: 'Zip',
        minWidth: 100,
        align: "left",
        display: 'flex',
    },
    {
        field: 'country',
        headerName: 'Country',
        minWidth: 100,
        align: "left",
        display: 'flex',
    },
    {
        field: 'peppol_id',
        headerName: 'Peppol ID',
        minWidth: 300,
        align: "left",
        display: 'flex',
    },
    {
        field: 'peppol_scheme',
        headerName: 'Peppol Scheme',
        minWidth: 150,
        align: "left",
        display: 'flex',
    },
    {
        field: 'peppol_supplier_id',
        headerName: 'Peppol supplier ID',
        minWidth: 200,
        align: "left",
        display: 'flex',
    },
    {
        field: 'invoice_type',
        headerName: 'Invoice Type',
        minWidth: 150,
        align: "left",
        display: 'flex',
    },
    {
        field: 'default_b2b_corp',
        headerName: 'Default B2B Corp',
        minWidth: 150,
        align: "left",
        display: 'flex',
    }
];

// Default mapping function for rows
const defaultRowMapper = (row) => ({
    id: row.id,
    name: row.name,
    address1: row.address1,
    address2: row.address2,
    vat: row.vat,
    zip: row.zip,
    city: row.city,
    country: row.country,
    peppol_id: row.peppol_id,
    peppol_scheme: row.peppol_scheme,
    peppol_supplier_id: row.peppol_supplier_id,
    invoice_type: row.invoice_type,
    default_b2b_corp: row.default_b2b_corp,
    actions: ""
});

const PeppolDataTable = ({
    data,
    loading,
    theme,
    checkboxSelection = true,
    showCheckVatAction = false,
    checkOnVat = null,
    onSelectionChange,
    customColumns = [],
    rowMapper = null,
    isRowSelectable = null,
    pagination = false,
    actionComponents = [],
    additionalProps = {}
}) => {
    const apiRef = useGridApiRef();

    // Prepare columns based on props
    let gridColumns = [];
    
    // Add checkbox selection column if needed
    if (checkboxSelection) {
        gridColumns.push({
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            width: 50,
        });
    }
    
    // Add actions column if there are actions
    if (showCheckVatAction || actionComponents.length > 0) {
        gridColumns.push({
            field: 'actions',
            type: 'actions',
            minWidth: 50,
            display: 'flex',
            getActions: (params) => {
                const actions = [];
                
                // Add Check VAT action if enabled
                if (showCheckVatAction && checkOnVat) {
                    if(!((params.row.peppol_id != "" && params.row.peppol_id != null && params.row.vat === "") || 
                       (params.row.peppol_id != "" && params.row.peppol_id != null && params.row.vat === null))) {
                        actions.push(
                            <GridActionsCellItem
                                icon={<ManageSearchIcon sx={{ color: Colors.sft_blue }} />}
                                label="Check VAT"
                                onClick={() => checkOnVat([params.row.vat], [params.row.id])}
                            />
                        );
                    }
                }
                
                // Add custom action components
                actionComponents.forEach(action => {
                    if (typeof action === 'function') {
                        const actionComponent = action(params);
                        if (actionComponent) {
                            actions.push(actionComponent);
                        }
                    }
                });
                
                return actions;
            },
        });
    }
    
    // Use custom columns if provided, otherwise use default columns
    if (customColumns.length > 0) {
        gridColumns = [...gridColumns, ...customColumns];
    } else {
        gridColumns = [...gridColumns, ...DEFAULT_COLUMNS];
    }

    // Format rows from data
    const rows = data.map(rowMapper || defaultRowMapper);

    // Default row selection criteria if not provided
    const defaultIsRowSelectable = (params) => !params.row.peppol_id || params.row.vat === "";

    return (
        <div className='rowContainerCenter _100perc'>
            <DataGridPro
                apiRef={apiRef}
                className={'excel_grid ' + theme}
                columns={gridColumns}
                checkboxSelection={checkboxSelection}
                rows={rows}
                isRowSelectable={isRowSelectable || defaultIsRowSelectable}
                initialState={{}}
                rowThreshold={0}
                rowHeight={38}
                autoHeight={true}
                loading={loading}
                pagination={pagination}
                onRowSelectionModelChange={(newSelection) => {
                    if (onSelectionChange) {
                        onSelectionChange(newSelection, apiRef);
                    }
                }}
                {...additionalProps}
            />
        </div>
    );
};

export default PeppolDataTable;